import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  Bell, AlertTriangle, TrendingUp, TrendingDown, Heart, 
  CheckCircle, X, ChevronRight, Clock, User, Trash2, 
  AlarmClock, Filter, Activity
} from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const SEVERITY_STYLES = {
  critical: {
    bg: 'bg-[#ff025f]/10',
    border: 'border-[#ff025f]/30',
    icon: 'text-[#ff025f]',
    badge: 'bg-[#ff025f]'
  },
  high: {
    bg: 'bg-[#ff025f]/10',
    border: 'border-[#ff025f]/30',
    icon: 'text-[#ff025f]',
    badge: 'bg-[#ff025f]'
  },
  medium: {
    bg: 'bg-[#02b5ff]/10',
    border: 'border-[#02b5ff]/30',
    icon: 'text-[#02b5ff]',
    badge: 'bg-[#02b5ff]'
  },
  low: {
    bg: 'bg-[#02b5ff]/10',
    border: 'border-[#02b5ff]/30',
    icon: 'text-[#02b5ff]',
    badge: 'bg-[#02b5ff]'
  }
};

const ALERT_TYPE_ICONS = {
  growth: TrendingUp,
  performance: Activity,
  mental_health: Heart,
  consent: CheckCircle,
  general: Bell
};

const ALERT_TYPE_LABELS = {
  growth: 'Growth',
  performance: 'Performance',
  mental_health: 'Mental Health',
  consent: 'Consent',
  general: 'General'
};

export default function AlertsPanel({ childId, compact = false }) {
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showSnoozeMenu, setShowSnoozeMenu] = useState(null);

  const loadAlerts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const params = new URLSearchParams();
      if (childId) params.append('child_id', childId);
      if (filter === 'unread') params.append('unread_only', 'true');
      if (typeFilter !== 'all') params.append('alert_type', typeFilter);
      
      const summaryParams = childId ? `?child_id=${childId}` : '';
      
      const [alertsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/alerts?${params.toString()}`, { headers }),
        axios.get(`${API}/alerts/summary${summaryParams}`, { headers })
      ]);
      
      setAlerts(alertsRes.data.alerts || []);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    } finally {
      setLoading(false);
    }
  }, [childId, filter, typeFilter]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const markAsRead = async (alertId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/alerts/${alertId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadAlerts();
    } catch (error) {
      toast.error('Failed to mark alert as read');
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/alerts/${alertId}/acknowledge`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Alert acknowledged');
      loadAlerts();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = childId ? `?child_id=${childId}` : '';
      await axios.put(`${API}/alerts/mark-all-read${params}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('All alerts marked as read');
      loadAlerts();
    } catch (error) {
      toast.error('Failed to mark alerts as read');
    }
  };

  const dismissAlert = async (alertId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/alerts/${alertId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Alert dismissed');
      loadAlerts();
    } catch (error) {
      toast.error('Failed to dismiss alert');
    }
  };

  const snoozeAlert = async (alertId, hours) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/alerts/${alertId}/snooze?hours=${hours}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Alert snoozed for ${hours} hours`);
      setShowSnoozeMenu(null);
      loadAlerts();
    } catch (error) {
      toast.error('Failed to snooze alert');
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const filteredAlerts = alerts.filter(alert => {
    // Status filter
    if (filter === 'unread' && alert.read) return false;
    if (filter === 'unacknowledged' && alert.acknowledged) return false;
    if (['critical', 'high', 'medium', 'low'].includes(filter) && alert.severity !== filter) return false;
    
    // Type filter
    if (typeFilter !== 'all' && alert.alert_type !== typeFilter) return false;
    
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#02b5ff]"></div>
      </div>
    );
  }

  // Compact mode for dashboard widget
  if (compact) {
    return (
      <div className="space-y-3" data-testid="alerts-compact">
        {/* Summary Bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-zinc-400" />
            <span className="text-sm text-zinc-400">
              {summary?.unread || 0} unread alerts
            </span>
          </div>
          {summary?.requires_attention && (
            <span className="flex items-center gap-1 text-xs text-[#ff025f]">
              <AlertTriangle className="h-3 w-3" />
              Requires attention
            </span>
          )}
        </div>
        
        {/* Recent Important Alerts */}
        {summary?.recent_important?.length > 0 ? (
          <div className="space-y-2">
            {summary.recent_important.slice(0, 3).map(alert => {
              const styles = SEVERITY_STYLES[alert.severity];
              const Icon = ALERT_TYPE_ICONS[alert.alert_type] || Bell;
              
              return (
                <div
                  key={alert.id}
                  className={`p-3 rounded-lg border ${styles.bg} ${styles.border}`}
                  data-testid={`alert-compact-${alert.id}`}
                >
                  <div className="flex items-start gap-2">
                    <Icon className={`h-4 w-4 mt-0.5 ${styles.icon}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{alert.title}</p>
                      <p className="text-xs text-zinc-400">{alert.child_name}</p>
                    </div>
                    <button
                      onClick={() => acknowledgeAlert(alert.id)}
                      className="text-zinc-400 hover:text-white"
                    >
                      <CheckCircle className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-zinc-500 text-center py-4">No important alerts</p>
        )}
      </div>
    );
  }

  // Full alerts panel
  return (
    <div className="space-y-4" data-testid="alerts-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#02b5ff]/20 rounded-lg flex items-center justify-center">
            <Bell className="h-5 w-5 text-[#02b5ff]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Alerts</h2>
            <p className="text-sm text-zinc-400">
              {summary?.unread || 0} unread • {summary?.critical_unacknowledged || 0} critical
            </p>
          </div>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={markAllRead}
          disabled={!summary?.unread}
        >
          Mark All Read
        </Button>
      </div>

      {/* Severity Summary */}
      <div className="grid grid-cols-4 gap-2">
        {['critical', 'high', 'medium', 'low'].map(severity => {
          const count = alerts.filter(a => a.severity === severity && !a.acknowledged).length;
          const styles = SEVERITY_STYLES[severity];
          
          return (
            <button
              key={severity}
              onClick={() => setFilter(filter === severity ? 'all' : severity)}
              className={`p-3 rounded-lg border transition-colors ${
                filter === severity 
                  ? `${styles.bg} ${styles.border}` 
                  : 'bg-zinc-900/30 border-zinc-800 hover:border-zinc-700'
              }`}
            >
              <div className={`w-2 h-2 rounded-full ${styles.badge} mb-1`}></div>
              <p className="text-lg font-bold text-white">{count}</p>
              <p className="text-xs text-zinc-500 capitalize">{severity}</p>
            </button>
          );
        })}
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 pb-2">
        <div className="flex gap-1 overflow-x-auto flex-1">
          {['all', 'unread', 'unacknowledged'].map(f => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className={`capitalize whitespace-nowrap ${filter === f ? 'bg-[#02b5ff] text-white' : ''}`}
            >
              {f === 'all' ? `All (${alerts.length})` : 
               f === 'unread' ? `Unread (${alerts.filter(a => !a.read).length})` :
               `Unack. (${alerts.filter(a => !a.acknowledged).length})`}
            </Button>
          ))}
        </div>
      </div>

      {/* Type Filter */}
      <div className="flex flex-wrap gap-2 pb-2">
        <span className="text-xs text-zinc-500 flex items-center gap-1">
          <Filter className="h-3 w-3" /> Type:
        </span>
        {['all', 'growth', 'performance', 'mental_health', 'consent'].map(type => (
          <button
            key={type}
            onClick={() => setTypeFilter(type)}
            className={`px-2 py-1 text-xs rounded-full transition-colors ${
              typeFilter === type 
                ? 'bg-[#02b5ff] text-white' 
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            {type === 'all' ? 'All Types' : ALERT_TYPE_LABELS[type] || type}
          </button>
        ))}
      </div>

      {/* Alerts List */}
      <div className="space-y-3">
        {filteredAlerts.length === 0 ? (
          <div className="text-center py-8 bg-zinc-900/30 rounded-lg border border-zinc-800">
            <Bell className="h-10 w-10 text-zinc-600 mx-auto mb-2" />
            <p className="text-zinc-500">No alerts to display</p>
          </div>
        ) : (
          filteredAlerts.map(alert => {
            const styles = SEVERITY_STYLES[alert.severity];
            const Icon = ALERT_TYPE_ICONS[alert.alert_type] || Bell;
            
            return (
              <div
                key={alert.id}
                className={`p-4 rounded-lg border transition-colors ${
                  !alert.read ? styles.bg : 'bg-zinc-900/20'
                } ${styles.border} ${alert.acknowledged ? 'opacity-60' : ''}`}
                data-testid={`alert-${alert.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    !alert.read ? styles.bg : 'bg-zinc-800'
                  }`}>
                    <Icon className={`h-4 w-4 ${styles.icon}`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-medium text-white">{alert.title}</h4>
                          <span className={`px-1.5 py-0.5 text-xs rounded capitalize ${styles.badge} text-white`}>
                            {alert.severity}
                          </span>
                          <span className="px-1.5 py-0.5 text-xs rounded bg-zinc-700 text-zinc-300 capitalize">
                            {ALERT_TYPE_LABELS[alert.alert_type] || alert.alert_type}
                          </span>
                        </div>
                        <p className="text-sm text-zinc-400 mt-1">{alert.message}</p>
                      </div>
                      
                      <div className="flex items-center gap-1 flex-shrink-0 relative">
                        {/* Snooze button with dropdown */}
                        <div className="relative">
                          <button
                            onClick={() => setShowSnoozeMenu(showSnoozeMenu === alert.id ? null : alert.id)}
                            className="p-1.5 hover:bg-zinc-800 rounded text-zinc-400 hover:text-[#02b5ff]"
                            title="Snooze"
                          >
                            <AlarmClock className="h-4 w-4" />
                          </button>
                          {showSnoozeMenu === alert.id && (
                            <div className="absolute right-0 top-8 bg-zinc-800 border border-zinc-700 rounded-lg shadow-lg z-10 py-1 min-w-[120px]">
                              {[1, 4, 8, 24, 48].map(hours => (
                                <button
                                  key={hours}
                                  onClick={() => snoozeAlert(alert.id, hours)}
                                  className="w-full px-3 py-1.5 text-left text-sm text-zinc-300 hover:bg-zinc-700"
                                >
                                  {hours < 24 ? `${hours}h` : `${hours/24}d`}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {!alert.read && (
                          <button
                            onClick={() => markAsRead(alert.id)}
                            className="p-1.5 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white"
                            title="Mark as read"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                        {!alert.acknowledged && (
                          <button
                            onClick={() => acknowledgeAlert(alert.id)}
                            className="p-1.5 hover:bg-[#02b5ff]/20 rounded text-zinc-400 hover:text-[#02b5ff]"
                            title="Acknowledge"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => dismissAlert(alert.id)}
                          className="p-1.5 hover:bg-[#ff025f]/20 rounded text-zinc-400 hover:text-[#ff025f]"
                          title="Dismiss"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {alert.child_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatTimeAgo(alert.created_at)}
                      </span>
                      {alert.acknowledged && (
                        <span className="flex items-center gap-1 text-[#02b5ff]">
                          <CheckCircle className="h-3 w-3" />
                          Acknowledged
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
